import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { allMsg, orStartingAtMonthlyMsg, t } from '../../common/i18n/index.js';
import { eurosToCents, formatSum } from '../../common/utils/priceUtils.js';
import { getLocalizedString, getProductOfferingPriceDataFromType } from '../../common/utils/productOfferingsUtils.js';
import { Grid } from '../Grid/Grid.js';
import { PriceType } from '../HorizontalSubscriptionCard/types.js';
import { getEnergyLabelRating, getLowestInstallmentOption } from '../ProductGridCoCo/productGridUtilsCoCo.js';
import { AccessoryProductCardCoCo } from './AccessoryProductCardCoCo.js';
import { DeviceDetailsAccessoryPicture } from './DeviceDetailsAccessoryPictureCoCo.js';
import { generateCartItemId } from './deviceDetailsUtilsCoCo.js';
import type { ProductOfferingCartItems } from '../../common/types/shoppingBasketCoCo.js';
import type { ProductOfferingCoco } from '../../generated/api/productOfferingCoco.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';

import './DeviceDetailsAccessoriesCoCo.scss';

interface Props {
  accessories: ProductOfferingCoco[];
}

export const DeviceDetailsAccessoriesCoCo = ({ accessories }: Props) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  if (!accessories.length) {
    return null;
  }

  const getAccessoryCategory = (a: (typeof accessories)[number]) =>
    a.productSpecification?.find(s => s.name === 'category')?.value?.[0]?.value;

  const categories = [...new Set(accessories.map(getAccessoryCategory).filter(Boolean))] as string[];

  const visible =
    selectedCategory === null ? accessories : accessories.filter(a => getAccessoryCategory(a) === selectedCategory);

  return (
    <div className="of-device-accessories">
      <Grid colWidthL={10}>
        <h2 className="ea-h2 of-device-accessories__title">{t.E83V('Compatible accessories')}</h2>

        <div className="of-device-accessories__filters">
          <CL.Tag
            type="selectable"
            title={t.EY6A(allMsg)}
            resultCount={String(accessories.length)}
            selected={selectedCategory === null}
            onClick={() => setSelectedCategory(null)}
          />
          {categories.map(cat => (
            <CL.Tag
              key={cat}
              type="selectable"
              title={cat}
              resultCount={String(accessories.filter(a => getAccessoryCategory(a) === cat).length)}
              selected={selectedCategory === cat}
              onClick={() => setSelectedCategory(cat)}
            />
          ))}
        </div>

        <CL.ProductGridV2>
          {visible
            .filter(accessory => !!getLocalizedString(accessory.name) && !!accessory.id)
            .map(accessory => {
              const name = getLocalizedString(accessory.name) ?? '';
              const price = getProductOfferingPriceDataFromType(
                accessory.productOfferingPrice,
                PriceType.ONE_TIME_CHARGE
              );
              const lowestInstallment = price ? getLowestInstallmentOption(price) : undefined;
              const installmentPrice = lowestInstallment?.priceWithoutTax;
              const energyLabelRating = getEnergyLabelRating(accessory as unknown as ProductOfferingsResponse);
              const basketItem: ProductOfferingCartItems = {
                id: accessory.id ?? '',
                name,
                quantity: 1,
                productOfferingPrice: accessory.productOfferingPrice,
                productOfferingType: accessory.productOfferingType,
                cartItemId: generateCartItemId(),
              };

              return (
                <AccessoryProductCardCoCo
                  key={accessory.id}
                  basketItem={basketItem}
                  productName={name}
                  productImage={<DeviceDetailsAccessoryPicture accessoryId={accessory.id ?? ''} alt={name} />}
                  productPrice={
                    <CL.Price
                      currentPrice={price?.priceWithoutTax?.toString() ?? ''}
                      currentPriceUnit="€"
                      additionalInfo={
                        installmentPrice === undefined
                          ? undefined
                          : t.OD1M(orStartingAtMonthlyMsg, formatSum(eurosToCents(installmentPrice), true) ?? '')
                      }
                      energyLabel={
                        energyLabelRating ? (
                          <CL.EnergyLabel rating={energyLabelRating} size="s" fluidSize i18n_energyLabel_label="" />
                        ) : undefined
                      }
                    />
                  }
                  onProductClick={() => {
                    // Accessory cards are display-only; navigation to be added in a future ticket
                  }}
                />
              );
            })}
        </CL.ProductGridV2>
      </Grid>
    </div>
  );
};
